<script setup>
import { computed } from '#imports';

const props = defineProps({
  newsItem: {
    type: Object,
    required: true,
  },
});

const formattedDate = computed(() =>
  new Date(props.newsItem.publishedAt).toLocaleString('ru-RU', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
);

const formattedTime = computed(() =>
  new Date(props.newsItem.publishedAt).toLocaleString('ru-RU', {
    hour: 'numeric',
    minute: 'numeric',
  })
);

const hasTags = computed(() => props.newsItem.tags?.length > 0);
const hasSource = computed(() => Boolean(props.newsItem.source?.name));
</script>

<template>
  <div class="news-item-header content-container">
    <ul v-if="hasTags" class="news-item-header__tags">
      <li
        v-for="tag in newsItem.tags"
        :key="tag._id"
        class="news-item-header__tag"
      >
        {{ tag.name }}
      </li>
    </ul>
    <div class="news-item-header__inner">
      <div class="news-item-header__column">
        <h1 class="news-item-header__title">{{ newsItem.title }}</h1>
      </div>
      <div class="news-item-header__column news-item-header__column--right">
        <div class="news-item-header__date-wrap">
          <div class="news-item-header__caption">Публикация</div>
          <div class="news-item-header__date">
            {{ formattedDate }}
            <span class="news-item-header__date-time">
              {{ formattedTime }}
            </span>
          </div>
        </div>
        <div v-if="hasSource" class="news-item-header__source-wrap">
          <div class="news-item-header__caption">Источник</div>
          <a
            v-if="newsItem.source.link"
            class="news-item-header__source news-item-header__source--link"
            :href="newsItem.source.link"
            target="_blank"
            >{{ newsItem.source.name }}</a
          >
          <div v-else class="news-item-header__source">
            {{ newsItem.source.name }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.news-item-header {
  padding: 34px 60px 0 228px;
  margin-bottom: 24px;

  @media (--tablet-vp) {
    padding: 34px 60px 0 60px;
  }

  &.content-container {
    max-width: var(--content-max-width);
  }

  &__inner {
    display: flex;
    gap: 32px;
  }

  &__title {
    max-width: 880px;
    font-family: var(--title-font-family);
    font-size: 42px;
    line-height: 50px;
    font-weight: 700;

    @media (--tablet-vp) {
      font-size: var(--h3-font-size);
      line-height: var(--h3-line-height);
    }
  }

  &__tags {
    display: flex;
    gap: 8px 20px;
    flex-wrap: wrap;
    max-width: 600px;
    margin-bottom: 4px;
  }

  &__tag {
    font-size: 18px;
    line-height: 22px;
    color: var(--accent-color);
  }

  &__column {
    &--right {
      padding-top: 16px;
    }
  }

  &__date-wrap {
    margin-bottom: 16px;
  }

  &__date {
    font-size: 18px;
    line-height: 26px;
  }

  &__date-time {
    color: rgba(var(--black-color--rgb), 40%);
  }

  &__caption {
    font-size: 14px;
    line-height: 22px;
    color: rgba(var(--black-color--rgb), 40%);
  }

  &__source {
    font-size: 18px;
    line-height: 26px;

    &--link {
      padding: 2px 4px;
      color: var(--accent-color);
      background-color: #f2e6f1;
      transition: var(--default-transition) color;
      border-radius: 4px;

      &:hover {
        color: rgba(var(--accent-color--rgb), 60%);
      }
    }
  }

  @media (--small-vp) {
    padding: 16px 12px 0;
    margin-bottom: 8px;

    &__tags {
      display: flex;
      gap: 4px 8px;
    }

    &__tag {
      font-size: 12px;
      line-height: 22px;
    }

    &__title {
      font-size: 24px;
      line-height: 24px;
    }

    &__inner {
      flex-direction: column;
      gap: 16px;
    }

    &__column {
      &--right {
        padding-top: 0;
        display: grid;
        grid-template-columns: 1fr 1fr;
        justify-content: flex-start;
        gap: 16px;
      }
    }

    &__caption {
      font-size: 12px;
      line-height: 15px;
    }

    &__date {
      font-size: 14px;
      line-height: 20px;
    }

    &__date-wrap {
      margin-bottom: 0;
    }

    &__source {
      padding: 0 3px;
      font-size: 14px;
      line-height: 20px;
    }
  }
}
</style>
